/**
 * Partner payouts, from request to paid.
 *
 *   requested → under review → approved (→ second approval) → processing → paid
 *                    └ rejected                                  └ failed
 *
 * Approving posts the TDS and GST lines from the rules in force that day — no
 * rule, no approval. Paid needs the bank's reference (UTR) and the date. A
 * failed payment takes the tax lines back and makes the entries payable again.
 * Each step is checked by the server against the payout's state under a lock,
 * and against who did the step before.
 */

import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Can,
  DetailField,
  DetailGrid,
  Drawer,
  Input,
  LoadingState,
  Modal,
  PageHeader,
  ServerTable,
  Tabs,
  createServerFetcher,
  formatDate,
  formatDateTime,
  formatMoney,
  todayISO,
  toast,
  type ServerColumn,
} from '@connect/web-shared';

import { LINE_KIND_LABELS, PAYOUT_STATUS, partnerKeys, partnersApi, type PayoutRow } from './api';
import { PayoutRuns } from './payout-runs';
import { Chip, Failed, ReasonDialog, errorText } from './shared';

export function PartnerPayoutsPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'runs' ? 'runs' : 'payouts';
  const [open, setOpen] = useState<string | null>(null);
  const fetchRows = useMemo(() => createServerFetcher<PayoutRow>('/partners/payouts'), []);
  const columns = useMemo<ServerColumn<PayoutRow>[]>(
    () => [
      { field: 'reference', header: 'Payout', filter: 'text', width: 150, render: (row) => <span className="font-mono text-xs">{row.reference}</span> },
      { field: 'partner', header: 'Partner', filter: 'text', minWidth: 180 },
      {
        field: 'status',
        header: 'Status',
        filter: 'select',
        options: Object.entries(PAYOUT_STATUS).map(([value, v]) => ({ value, label: v.label })),
        width: 140,
        render: (row) => <Chip value={row.status} map={PAYOUT_STATUS} />,
      },
      {
        field: 'trigger',
        header: 'How',
        filter: 'select',
        options: [
          { value: 'scheduled', label: 'Scheduled' },
          { value: 'request', label: 'Asked for' },
        ],
        width: 120,
        render: (row) => (row.trigger === 'scheduled' ? `Scheduled ${row.scheduled_for ? formatDate(row.scheduled_for) : ''}` : 'Asked for'),
      },
      { field: 'gross', header: 'Gross', sortable: false, numeric: true, width: 120, format: (v) => formatMoney(v as string) },
      { field: 'net', header: 'Net', filter: 'number', numeric: true, width: 120, format: (v) => formatMoney(v as string) },
      { field: 'requested_at', header: 'Asked', filter: 'date', width: 130, format: (v) => formatDate(v as string) },
    ],
    [],
  );
  return (
    <div className="flex flex-1 flex-col gap-4">
      <PageHeader
        title="Partner payouts"
        description="Review, approve and record what is paid to partners, and the scheduled runs."
        tabs={
          <Tabs
            value={tab}
            onChange={(id) => setParams({ tab: id }, { replace: true })}
            items={[
              { id: 'payouts', label: 'Payouts' },
              { id: 'runs', label: 'Scheduled runs' },
            ]}
          />
        }
      />
      {tab === 'runs' ? <PayoutRuns /> : null}
      {tab === 'payouts' ? (
      <ServerTable<PayoutRow>
        columns={columns}
        fetchRows={fetchRows}
        rowKey={(row) => row.public_id}
        queryKey={partnerKeys.payouts}
        stateKey="partners.payouts"
        searchPlaceholder="Search by partner or reference"
        onView={(row) => setOpen(row.public_id)}
        emptyTitle="No payouts"
        emptyDescription="When a run is prepared or a partner asks to be paid, the payout appears here."
      />
      ) : null}
      <PayoutDrawer id={open} onClose={() => setOpen(null)} />
    </div>
  );
}

function PayoutDrawer({ id, onClose }: { id: string | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const detail = useQuery({ queryKey: partnerKeys.payout(id ?? ''), queryFn: () => partnersApi.payout(id!), enabled: id !== null });
  const [reasonFor, setReasonFor] = useState<'reject' | 'fail' | null>(null);
  const [paying, setPaying] = useState(false);
  const [reference, setReference] = useState('');
  const [paidOn, setPaidOn] = useState(todayISO());
  const done = async (message: string) => {
    toast.success(message);
    setReasonFor(null);
    setPaying(false);
    await queryClient.invalidateQueries({ queryKey: partnerKeys.all });
  };
  const step = useMutation({
    mutationFn: (action: 'review' | 'approve' | 'processing') => partnersApi.payoutAction(id!, action),
    onSuccess: () => done('Saved'),
    onError: (e) => toast.error('Could not do that', errorText(e)),
  });
  const withReason = useMutation({
    mutationFn: (reason: string) =>
      reasonFor === 'reject' ? partnersApi.rejectPayout(id!, reason) : partnersApi.failPayout(id!, reason),
    onSuccess: () => done(reasonFor === 'reject' ? 'Payout rejected' : 'Payout marked failed'),
  });
  const paid = useMutation({
    mutationFn: () => partnersApi.paid(id!, { payment_reference: reference.trim(), paid_on: paidOn }),
    onSuccess: () => done('Payout recorded as paid'),
  });

  const data = detail.data;
  const p = data?.payout;
  return (
    <Drawer open={id !== null} onClose={onClose} title={p ? `Payout ${p.reference}` : 'Payout'} width="lg">
      {detail.isPending ? (
        <LoadingState />
      ) : detail.isError ? (
        <Failed error={detail.error} />
      ) : p && data ? (
        <div className="space-y-4">
          <DetailGrid>
            <DetailField label="Partner">{p.partner}</DetailField>
            <DetailField label="Status">
              <Chip value={p.status} map={PAYOUT_STATUS} />
            </DetailField>
            <DetailField label="Gross">{formatMoney(p.gross)}</DetailField>
            <DetailField label="TDS">{formatMoney(p.tds)}</DetailField>
            <DetailField label="GST">{formatMoney(p.gst)}</DetailField>
            <DetailField label="Net">{formatMoney(p.net)}</DetailField>
            <DetailField label="Asked">{formatDateTime(p.requested_at)}</DetailField>
            <DetailField label="Approved">{formatDateTime(p.approved_at)}</DetailField>
            <DetailField label="Second approval">
              {p.needs_second_approval ? (p.second_approved_at ? formatDateTime(p.second_approved_at) : 'Needed') : 'Not needed'}
            </DetailField>
            <DetailField label="Bank reference">{p.payment_reference}</DetailField>
          </DetailGrid>
          <div className="rounded-md bg-surface-2 p-3 text-sm">
            <p className="font-medium">Pays to</p>
            <p className="text-muted">
              {data.payout_details.bank_holder ?? '—'} · {data.payout_details.bank_account ?? '—'} · {data.payout_details.bank_ifsc ?? '—'} ·{' '}
              details {data.payout_details.status}
            </p>
          </div>
          {p.note ? <Alert tone="info">{p.note}</Alert> : null}

          <div className="flex flex-wrap gap-2">
            {p.status === 'requested' ? (
              <Can permission="partners.payout.approve">
                <Button loading={step.isPending} onClick={() => step.mutate('review')}>
                  Start review
                </Button>
              </Can>
            ) : null}
            {p.status === 'under_review' || (p.status === 'approved' && p.needs_second_approval && !p.second_approved_at) ? (
              <Can permission="partners.payout.approve">
                <Button loading={step.isPending} onClick={() => step.mutate('approve')}>
                  {p.status === 'approved' ? 'Second approval' : 'Approve (posts TDS)'}
                </Button>
              </Can>
            ) : null}
            {p.status === 'requested' || p.status === 'under_review' ? (
              <Can permission="partners.payout.approve">
                <Button variant="danger" onClick={() => setReasonFor('reject')}>
                  Reject
                </Button>
              </Can>
            ) : null}
            {p.status === 'approved' && (!p.needs_second_approval || p.second_approved_at) ? (
              <Can permission="partners.payout.manage">
                <Button loading={step.isPending} onClick={() => step.mutate('processing')}>
                  Mark as being paid
                </Button>
              </Can>
            ) : null}
            {p.status === 'processing' ? (
              <Can permission="partners.payout.manage">
                <Button onClick={() => setPaying(true)}>Record payment</Button>
                <Button variant="danger" onClick={() => setReasonFor('fail')}>
                  Payment failed
                </Button>
              </Can>
            ) : null}
          </div>

          <div>
            <h3 className="mb-2 text-sm font-medium">What it pays</h3>
            <table className="w-full text-sm">
              <tbody>
                {data.entries.map((e) => (
                  <tr key={e.public_id} className="border-t border-line">
                    <td className="py-1.5 font-mono text-xs text-muted">{e.reference}</td>
                    <td className="py-1.5">{e.client ?? LINE_KIND_LABELS[e.kind]}</td>
                    <td className="py-1.5 text-xs text-muted">{e.description}</td>
                    <td className="py-1.5 text-right tabular-nums">{formatMoney(e.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <ReasonDialog
        open={reasonFor !== null}
        title={reasonFor === 'reject' ? 'Reject this payout' : 'The payment failed'}
        description={
          reasonFor === 'reject'
            ? 'The partner sees this reason; its commission becomes payable again.'
            : 'The TDS and GST lines are taken back and the commission becomes payable again.'
        }
        confirmLabel={reasonFor === 'reject' ? 'Reject' : 'Mark failed'}
        variant="danger"
        busy={withReason.isPending}
        error={withReason.error}
        onClose={() => setReasonFor(null)}
        onConfirm={(reason) => withReason.mutate(reason)}
      />
      <Modal
        open={paying}
        onClose={() => setPaying(false)}
        title="Record the payment"
        description={p ? `${formatMoney(p.net)} to the partner's verified account. Paid is recorded only with the bank's reference.` : undefined}
        footer={
          <>
            <Button variant="secondary" onClick={() => setPaying(false)}>
              Cancel
            </Button>
            <Button loading={paid.isPending} disabled={reference.trim().length < 4 || !paidOn} onClick={() => paid.mutate()}>
              Record as paid
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {paid.isError ? <Alert tone="danger">{errorText(paid.error)}</Alert> : null}
          <Input label="Bank reference (UTR)" value={reference} onChange={(e) => setReference(e.target.value)} />
          <Input label="Paid on" type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
        </div>
      </Modal>
    </Drawer>
  );
}
